import React, { useContext, useState, useEffect } from 'react';
import { StateContext } from '../../../../StateProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import Popup from '../../../../Utilities/components/Popup';
import { redeemReferralCode } from '../../../../Utilities/api';
import './Invite.css';

import inviteBackground from '../../../../../assets/Store/Deals/Invite/inviteBackground.png';
import { XButton } from '../../../../Utilities/components/Buttons';

const Invite = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setLoading, refreshUserData } = useContext(StateContext);

  // --- Referral State ---
  const [referrals, setReferrals] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [shareLink, setShareLink] = useState('');

  // --- Popup State ---
  const [copyButtonText, setCopyButtonText] = useState('Copy');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [message, setMessage] = useState('');

  // --- Milestones ---
  const milestones = [5, 10, 15];
  const maxReferrals = milestones[milestones.length - 1];

  var progress = (referrals / maxReferrals) * 100;
  if (progress < 0) progress = 0;
  if (progress > 100) progress = 100;

  // --- Initial Load / Redeem From URL ---
  useEffect(function () {
    const params = new URLSearchParams(location.search);
    const codeFromURL = params.get('redeemCode');

    if (codeFromURL) {
      setRedeemCode(codeFromURL);
    }

    if (user) {
      setReferrals(user.referralsCount || 0);
      setReferralCode(user.referralCode || '');

      if (codeFromURL) {
        handleRedeem(codeFromURL);
      }
    } else if (codeFromURL) {
      navigate(
        `/login?redirect=${encodeURIComponent(
          location.pathname + location.search
        )}`
      );
    }
  }, [user]);

  // --- Share Link ---
  useEffect(function () {
    if (referralCode) {
      setShareLink(
        `https://pawsible.app/store/deals/invite?code=${referralCode}`
      );
    }

    if (user && user.uid) {
      refreshUserData(user.uid);
    }
  }, [referralCode]);

  // --- Popup Helpers ---
  function togglePopup(content) {
    setPopupContent(content || '');
    setIsPopupOpen(!isPopupOpen);
  }

  function handleRedeemCode() {
    togglePopup('redeemCode');
  }

  function handleShareLink() {
    togglePopup('shareLink');
  }

  // --- Redeem Logic ---
  async function handleRedeem(codeOverride) {
    if (codeOverride && typeof codeOverride === 'object') {
      codeOverride = null;
    }

    var finalCode = codeOverride || redeemCode;

    if (!finalCode || typeof finalCode !== 'string') {
      setMessage('Please enter a referral code.');
      return;
    }

    setLoading(true);
    try {
      const response = await redeemReferralCode(finalCode, user);

      if (response && response.success) {
        setMessage(response.message || 'Code redeemed successfully!');
        refreshUserData(user.uid);
        togglePopup();
      } else {
        setMessage(response.message || 'Invalid referral code.');
      }
    } catch (error) {
      console.error('Error redeeming code:', error);
      setMessage('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // --- Copy RAW referral code (unchanged) ---
  function handleCopyLink() {
    navigator.clipboard
      .writeText(referralCode)
      .then(function () {
        setCopyButtonText('Woof!');
        setTimeout(function () {
          setCopyButtonText('Copy');
        }, 2000);
      })
      .catch(function () {
        alert('Failed to copy the referral code.');
      });
  }

  // --- Share Logic (unchanged) ---
  function handlePopupShareLink() {
    var formattedMessage =
      'Hey! I found this amazing place for pets called Pawsible. 🐾\n\n' +
      'Join now and get a $30 Welcome Voucher!\n\n' +
      'Here’s my referral link:\n' +
      shareLink;

    if (navigator.share) {
      navigator
        .share({
          title: 'Invite to Pawsible!',
          text: formattedMessage,
        })
        .catch(function (error) {
          console.error('Sharing failed:', error);
        });
    } else {
      navigator.clipboard.writeText(formattedMessage);
    }

    togglePopup();
  }

  return (
    <div>
      <XButton />

      <img
        className="invite-hero-image"
        src={inviteBackground}
        alt="Invite"
      />

      <div className="invite-container">
        <h2 className="invite-header">Refer & Earn</h2>

        <div className="reward-container">
          <div className="referrals-count">
            <span className="highlight-referrals">{referrals}</span> REFERRALS
          </div>

          <div className="rewards">
            {milestones.map(function (milestone, index) {
              var rewardAmount =
                index === 0 ? '$50' : index === 1 ? '$175' : '$425';

              return (
                <div
                  key={index}
                  className={
                    'reward-card ' +
                    (referrals >= milestone ? 'highlighted' : 'greyed-out')
                  }
                >
                  <div className="reward-amount">{rewardAmount}</div>
                  <div className="reward-message">Pawsible Tokens</div>
                </div>
              );
            })}
          </div>

          <div className="reward-progress-bar-container">
            <div
              className="reward-progress-bar"
              style={{ width: progress + '%' }}
            ></div>

            <div className="milestone highlighted" style={{ left: '0%' }}>
              0
            </div>

            {milestones.map(function (milestone, index) {
              return (
                <div
                  key={index}
                  className={
                    'milestone ' +
                    (referrals >= milestone ? 'highlighted' : 'greyed-out')
                  }
                  style={{
                    left: (milestone / maxReferrals) * 100 + '%',
                  }}
                >
                  {milestone}
                </div>
              );
            })}
          </div>
        </div>

        {message && <p className="invite-popup-message">{message}</p>}

        <button className="redeem-btn" onClick={handleRedeemCode}>
          Redeem Code
        </button>

        <p className="invite-paragraph">
          Share Pawsible with your friends. They get{' '}
          <span className="highlight-invite">$30 Welcome Voucher</span> and you
          earn rewards when you <strong>reach the milestones!</strong>
        </p>

        <div className="referral-code">
          <input type="text" value={referralCode} readOnly />
          <button className="copy-btn" onClick={handleCopyLink}>
            {copyButtonText}
          </button>
        </div>

        {/* ✅ SHARE BUTTON (THIS WAS MISSING) */}
        <div className="invite-actions">
          <button
            className="share-btn"
            onClick={handleShareLink}
            disabled={!shareLink}
          >
            Share Invite
          </button>
        </div>
      </div>

      <Popup isOpen={isPopupOpen} handleClose={() => setIsPopupOpen(false)}>
        {popupContent === 'redeemCode' && (
          <div className="invite-popup">
            <h2 className="invite-popup-header">Redeem a Code</h2>
            <input
              type="text"
              placeholder="Enter referral code"
              value={redeemCode}
              onChange={function (e) {
                setRedeemCode(e.target.value);
              }}
            />
            {message && <p className="invite-popup-message">{message}</p>}
            <div className="invite-popup-actions">
              <button
                onClick={function () {
                  handleRedeem();
                }}
                className="invite-popup-yes-button"
              >
                Redeem
              </button>
              <button
                onClick={function () {
                  setIsPopupOpen(false);
                }}
                className="invite-popup-no-button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {popupContent === 'shareLink' && (
          <div className="invite-popup">
            <h2 className="invite-popup-header">Share Referral Link</h2>
            <input type="text" value={shareLink} readOnly />
            <div className="invite-popup-actions">
              <button
                onClick={handlePopupShareLink}
                className="invite-popup-yes-button"
              >
                {navigator.share ? 'Share' : 'Copy'}
              </button>
              <button
                onClick={function () {
                  setIsPopupOpen(false);
                }}
                className="invite-popup-no-button"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Popup>
    </div>
  );
};

export default Invite;
